import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Loader2, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, humanize } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { DemoBadge, Field, ListInput, PageHeader, QueryState, StatusBadge } from "@/components/common";
import RecordDialog, { useCrud } from "@/components/RecordDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const LABELS = {
  company_name: "Company name", markets_served: "Markets served", products_services: "Products and services", icp: "Ideal customer profile",
  segments: "Customer segments", buyer_roles: "Buyer roles", value_proposition: "Value proposition", proof_points: "Proof points",
  attributes: "Voice attributes", preferred_terms: "Preferred terminology", prohibited_terms: "Prohibited terminology", approved_examples: "Approved examples",
  primary_message: "Primary message", supporting_messages: "Supporting messages", pillars: "Messaging pillars", approved_claims: "Approved claims",
  claims_requiring_evidence: "Claims requiring evidence", prohibited_claims: "Prohibited claims", ctas: "Calls to action", logo_url: "Logo URL",
  colours: "Brand colours (hex, one per line)", screenshots: "Product screenshot URLs", approved_imagery: "Approved imagery", creative_guidance: "Creative guidance",
};
const label = (k) => LABELS[k] || humanize(k);
const TABS = ["company", "audience", "positioning", "offers", "voice", "messaging", "visual", "review"];
const LONG = ["description", "icp", "value_proposition", "creative_guidance", "primary_message", "tone"];

function SectionForm({ section, schema, data }) {
  const { path, wid } = useWorkspace();
  const qc = useQueryClient();
  const [form, setForm] = useState(data || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(data || {}), [data]);
  const save = async () => {
    setBusy(true);
    try { await api.put(path(`/brand/${section}`), form); toast.success("Brand Brain updated"); qc.invalidateQueries({ queryKey: ["ws", wid] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="cq-card p-5 sm:p-7" data-testid={`brand-section-${section}`}>
      <div className="grid md:grid-cols-2 gap-5">
        {Object.entries(schema).map(([k, kind]) => (
          <div key={k} className={LONG.includes(k) ? "md:col-span-2" : ""}>
            <Field label={label(k)}>
              {kind === "list" ? <ListInput testId={`brand-field-${k}`} rows={3} value={form[k]} onChange={(v) => setForm({ ...form, [k]: v })} /> :
                LONG.includes(k) ? <textarea data-testid={`brand-field-${k}`} rows={3} className="cq-input" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /> :
                  <input data-testid={`brand-field-${k}`} className="cq-input" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />}
            </Field>
          </div>))}
      </div>
      {section === "visual" && (form.colours || []).length > 0 && (
        <div className="mt-5 flex gap-2">{form.colours.filter((c) => /^#[0-9a-f]{3,8}$/i.test(c)).map((c) => <span key={c} title={c} className="w-9 h-9 rounded-lg border border-[#243153]" style={{ background: c }} />)}</div>
      )}
      <div className="mt-6 flex justify-end"><button data-testid={`brand-save-${section}`} disabled={busy} onClick={save} className="cq-btn cq-btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save section</button></div>
    </div>
  );
}

function RecordList({ resource, title, fields, titleKey, render }) {
  const q = useWsQuery(`/r/${resource}`);
  const crud = useCrud(resource);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  return (
    <div className="cq-card p-5 sm:p-7" data-testid={`brand-records-${resource}`}>
      <div className="flex items-center justify-between mb-4"><h3 className="font-display text-lg text-[#F7FAFF]">{title}</h3>
        <button data-testid={`brand-add-${resource}`} onClick={() => setEdit({})} className="cq-btn cq-btn-ghost !py-1.5"><Plus size={13} /> Add</button></div>
      <QueryState q={q} empty={<p className="text-sm text-[#7C8BAD]">None yet.</p>}>
        {(items) => <div className="space-y-2">{items.map((it) => (
          <div key={it.id} data-testid={`brand-record-${it.id}`} className="p-4 rounded-lg border border-[#172342] flex items-start gap-3">
            <div className="flex-1 min-w-0"><div className="text-sm font-medium text-[#F7FAFF]">{it[titleKey]} {it.is_demo && <span className="font-mono text-[9.5px] text-[#F4B860]">DEMO</span>}</div>{render(it)}</div>
            <button data-testid={`brand-edit-${it.id}`} onClick={() => setEdit(it)} className="cq-btn cq-btn-ghost !p-1.5"><Pencil size={13} /></button>
            <button data-testid={`brand-delete-${it.id}`} onClick={() => setDel(it)} className="cq-btn cq-btn-ghost !p-1.5"><Trash2 size={13} /></button>
          </div>))}</div>}
      </QueryState>
      <RecordDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? `Edit ${title.toLowerCase()}` : `Add ${title.toLowerCase()}`} fields={fields} initial={edit}
        onSubmit={(b) => (edit?.id ? crud.update(edit.id, b) : crud.create(b))} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} danger title="Archive this record?" description="It will be soft-deleted. This is logged." confirmLabel="Archive" onConfirm={() => crud.remove(del.id)} />
    </div>
  );
}

const OFFER_FIELDS = [{ k: "name", label: "Name" }, { k: "description", label: "Description", type: "textarea", rows: 3 }, { k: "audience", label: "Audience", half: true },
  { k: "benefit", label: "Benefit", half: true }, { k: "price", label: "Price", half: true }, { k: "cta", label: "CTA", half: true },
  { k: "landing_page_url", label: "Landing-page URL" }, { k: "status", label: "Status", type: "select", options: ["draft", "active", "paused", "retired"].map((s) => [s, humanize(s)]) }];
const AUD_FIELDS = [{ k: "name", label: "Segment name" }, { k: "description", label: "Description", type: "textarea", rows: 3 }, { k: "geography", label: "Geography", half: true },
  { k: "size_estimate", label: "Size estimate", half: true }, { k: "buyer_roles", label: "Buyer roles", type: "list" }, { k: "problems", label: "Problems", type: "list" }, { k: "is_primary", label: "Primary segment", type: "bool" }];
const PILLAR_FIELDS = [{ k: "title", label: "Pillar" }, { k: "description", label: "Description", type: "textarea", rows: 3 }, { k: "proof_points", label: "Proof points", type: "list" }];

function Review({ brand }) {
  const c = brand.completion;
  return (
    <div className="space-y-4" data-testid="brand-review">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(c.sections).map(([s, pct]) => (
          <div key={s} className="cq-card p-4"><div className="flex items-center justify-between"><span className="text-sm text-[#F7FAFF]">{humanize(s)}</span><span className="font-mono text-xs text-[#22E6F2]">{pct}%</span></div>
            <div className="mt-2 h-1 rounded-full bg-[#172342]"><div className="h-full rounded-full bg-[#22E6F2]" style={{ width: `${pct}%` }} /></div></div>))}
      </div>
      {Object.entries(brand.schema).map(([sec, fields]) => (
        <div key={sec} className="cq-card p-5"><div className="cq-label mb-3">{sec}</div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">{Object.keys(fields).map((f) => {
            const v = brand[sec]?.[f]; const filled = Array.isArray(v) ? v.length : !!v;
            return (<div key={f} className="flex gap-2.5 text-sm py-1">{filled ? <CheckCircle2 size={15} className="text-[#4BE3A4] shrink-0 mt-0.5" /> : <Circle size={15} className="text-[#3A4870] shrink-0 mt-0.5" />}
              <div className="min-w-0"><span className="text-[#AAB6CC]">{label(f)}: </span><span className="text-[#F7FAFF]">{filled ? (Array.isArray(v) ? v.join(", ") : v) : <em className="text-[#5D6B8C]">missing</em>}</span></div></div>);
          })}</div></div>))}
    </div>
  );
}

export default function BrandBrain() {
  const q = useWsQuery("/brand");
  const { current } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "company";
  return (
    <div>
      <PageHeader eyebrow="Context" title="Brand Brain" description="The source of truth CRESQEN uses when interpreting commands and drafting strategy."
        actions={current?.is_demo && <DemoBadge label="Partially completed demo profile" />} />
      <QueryState q={q}>
        {(brand) => (<>
          <div className="cq-card p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="brand-completion">
            <div className="font-display text-4xl font-semibold cq-gradient-text" data-testid="brand-completion-pct">{brand.completion.overall}%</div>
            <div className="flex-1"><div className="text-sm text-[#F7FAFF]">Brand Brain completion</div><div className="mt-2 h-1.5 rounded-full bg-[#172342]"><div className="h-full rounded-full" style={{ width: `${brand.completion.overall}%`, background: "var(--cq-gradient)" }} /></div></div>
          </div>
          <div className="flex gap-1 overflow-x-auto mb-6 border-b border-[#243153]" data-testid="brand-tabs">
            {TABS.map((t) => (
              <button key={t} data-testid={`brand-tab-${t}`} onClick={() => setParams({ tab: t })} className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t ? "border-[#22E6F2] text-[#F7FAFF]" : "border-transparent text-[#AAB6CC] hover:text-[#F7FAFF]"}`}>
                {humanize(t)} {brand.completion.sections[t] !== undefined && <span className="font-mono text-[10px] text-[#7C8BAD] ml-1">{brand.completion.sections[t]}%</span>}
              </button>))}
          </div>
          {tab === "review" ? <Review brand={brand} /> : tab === "offers" ? (
            <RecordList resource="offers" title="Offers" titleKey="name" fields={OFFER_FIELDS}
              render={(o) => <div className="mt-1 text-xs text-[#AAB6CC] space-y-1"><p>{o.description}</p><div className="flex flex-wrap gap-3 items-center"><StatusBadge status={o.status} /><span>{o.price}</span><span>CTA: {o.cta}</span><span className="text-[#22E6F2] truncate">{o.landing_page_url}</span></div></div>} />
          ) : (
            <div className="space-y-6">
              <SectionForm section={tab} schema={brand.schema[tab]} data={brand[tab]} />
              {tab === "audience" && <RecordList resource="audiences" title="Audience segments" titleKey="name" fields={AUD_FIELDS} render={(a) => <p className="text-xs text-[#AAB6CC] mt-1">{a.geography} · {a.description} {a.is_primary && "· Primary"}</p>} />}
              {tab === "messaging" && <RecordList resource="messaging_pillars" title="Messaging pillars" titleKey="title" fields={PILLAR_FIELDS} render={(p) => <p className="text-xs text-[#AAB6CC] mt-1">{p.description}</p>} />}
            </div>
          )}
        </>)}
      </QueryState>
    </div>
  );
}
